/**
 * DeepInfra LLM Provider - v13 Section 6.10-6.11
 *
 * Provider for DeepInfra's cost-effective LLM inference.
 *
 * Key features:
 * - Zero Data Retention by DEFAULT
 * - SOC 2 + ISO 27001 certified
 * - ~10x cheaper than OpenAI
 * - OpenAI-compatible API format
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
import { BaseLLMProvider, LLMProviderType } from './base';
import { calculateCost } from './registry';
/**
 * DeepInfra LLM Provider - Uses OpenAI-compatible API
 */
export class DeepInfraProvider extends BaseLLMProvider {
    constructor(config) {
        super(config);
        if (!config.apiKey) {
            throw new Error('DeepInfra API key is required');
        }
        this.apiKey = config.apiKey;
        this.baseUrl = config.baseUrl ?? 'https://api.deepinfra.com/v1/openai';
        // Model should be explicitly configured via config.model or request.model
        // Empty default will cause error if no model specified - this is intentional
        this.defaultModel = config.model ?? '';
        // Token limit should come from ConfigService.getModelMetadata() at runtime
        this.defaultMaxTokens = config.maxTokens ?? 4096;
        this.defaultTemperature = config.temperature ?? 0.7;
    }
    getProviderType() {
        return LLMProviderType.DEEPINFRA;
    }
    /**
     * Get supported models
     * @deprecated Use configService.getModelsByProvider('deepinfra') for dynamic model list
     */
    getSupportedModels() {
        // No hardcoded model list - use ConfigService for actual list
        return [];
    }
    async isAvailable() {
        try {
            const response = await fetch(`${this.baseUrl}/models`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${this.apiKey}`,
                },
            });
            return response.ok;
        }
        catch (error) {
            this.logger.debug(`DeepInfra availability check failed: ${error}`);
            return false;
        }
    }
    async complete(request) {
        const model = request.model ?? this.defaultModel;
        // Trust caller's maxTokens if provided, otherwise use default
        const maxTokens = request.maxTokens ?? this.defaultMaxTokens;
        const temperature = request.temperature ?? this.defaultTemperature;
        // Validate request
        const errors = this.validateRequest(request);
        if (errors.length > 0) {
            return this.createErrorResponse(errors.join('; '), model);
        }
        const messages = request.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
        }));
        this.logger.info(`Using model: ${model} for DeepInfra request`);
        const startTime = Date.now();
        try {
            const response = await fetch(`${this.baseUrl}/chat/completions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.apiKey}`,
                },
                body: JSON.stringify({
                    model,
                    messages,
                    max_tokens: maxTokens,
                    temperature,
                }),
            });
            if (!response.ok) {
                const error = await response.text();
                throw new Error(`DeepInfra API error: ${response.status} ${error}`);
            }
            const data = await response.json();
            const processingTime = (Date.now() - startTime) / 1000;
            const content = data.choices?.[0]?.message?.content ?? '';
            const inputTokens = data.usage?.prompt_tokens ?? 0;
            const outputTokens = data.usage?.completion_tokens ?? 0;
            const totalTokens = data.usage?.total_tokens ?? 0;
            const costUsd = calculateCost(model, inputTokens, outputTokens);
            this.logger.debug('Received response from DeepInfra', {
                model,
                content_length: content.length,
                processing_time: processingTime,
                tokens_used: totalTokens,
                cost: costUsd,
            });
            return {
                content,
                model,
                usage: {
                    inputTokens,
                    outputTokens,
                    totalTokens,
                    costUsd,
                },
                finishReason: data.choices?.[0]?.finish_reason,
            };
        }
        catch (error) {
            const processingTime = (Date.now() - startTime) / 1000;
            const errorMsg = `DeepInfra API error: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error('DeepInfra generation failed', { model, error: errorMsg, processingTime });
            return this.createErrorResponse(errorMsg, model);
        }
    }
}
